function resolveApiBase() {
  const raw = (process.env.REACT_APP_BACKEND_URL || 'https://qa.yshd.in').trim(); // 'http://216.48.184.80:3001' 'http://localhost:3001' 'https://audiotestapi.yshd.in' 'https://audioapi.yshd.in' 'https://qa.yshd.in'
  const base = raw.replace(/\/+$/, '');
  return base.endsWith('/api') ? base : `${base}/api`;
}

const API_BASE = resolveApiBase();
const inFlightGetRequests = new Map();

function getToken() {
  return sessionStorage.getItem('vcm_token');
}

async function apiFetch(path, options = {}) {
  const token = getToken();
  const method = (options.method || 'GET').toUpperCase();
  const requestUrl = `${API_BASE}${path}`;
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const executeRequest = async () => {
    const res = await fetch(requestUrl, { ...options, method, headers });

    if (res.status === 401) {
      sessionStorage.removeItem('vcm_token');
      sessionStorage.removeItem('vcm_user');
      window.location.href = '/login';
      return;
    }

    if (!res.ok) {
      const errText = await res.text();
      // Most controllers return JSON like {error: '...'}; surface that
      // directly so toasts show a readable message instead of raw JSON.
      let message = errText || `HTTP ${res.status}`;
      try {
        const parsed = JSON.parse(errText);
        if (parsed && typeof parsed.error === 'string') message = parsed.error;
      } catch { /* not JSON — keep raw text */ }
      const err = new Error(message);
      err.status = res.status;
      throw err;
    }

    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('text/csv')) {
      return res.blob();
    }
    return res.json();
  };

  if (method !== 'GET') {
    return executeRequest();
  }

  // De-duplicate concurrent GET requests for the same URL and token.
  // This avoids duplicate network calls from React StrictMode remounts.
  const requestKey = `${method}:${requestUrl}:${token || ''}`;
  if (inFlightGetRequests.has(requestKey)) {
    return inFlightGetRequests.get(requestKey);
  }

  const requestPromise = executeRequest().finally(() => {
    inFlightGetRequests.delete(requestKey);
  });

  inFlightGetRequests.set(requestKey, requestPromise);
  return requestPromise;
}

export const api = {
  // Auth
  login: (email, password) => apiFetch('/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  getMe: () => apiFetch('/me'),
  
  // Calls
  getCalls: (params) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
    return apiFetch(`/calls?${qs.toString()}`);
  },
  getCallDetail: (callId) => apiFetch(`/calls/${encodeURIComponent(callId)}`),
  getCallLanguages: () => apiFetch('/calls/languages'),
  rerunCall: (callId) => apiFetch(`/calls/${encodeURIComponent(callId)}/rerun`, { method: 'POST' }),
  
  // Dashboard
  getDashboard: (from, to) => apiFetch(`/dashboard/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),

  // Agents
  getAgentsList: () => apiFetch('/agents/list'),
  getAgentAnalytics: (name, from, to) => apiFetch(`/agents/${encodeURIComponent(name)}/analytics?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  
  // Coaching (agent + supervisor screen). Always scoped to the logged-in user's
  // own calls — the server resolves the agent from the token, never from here.
  // One read-only endpoint: the whole screen is built from stored analysis, so
  // there is nothing to generate and nothing that costs a model call.
  getCoachingOverview: (from, to) =>
    apiFetch(`/coaching/overview?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),

  // Users
  getUsers: () => apiFetch('/users'),
  createUser: (data) => apiFetch('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => apiFetch(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id) => apiFetch(`/users/${id}`, { method: 'DELETE' }),
  syncAgents: () => apiFetch('/users/sync-agents', { method: 'POST' }),
  
  // Reports
  getReport: (params) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v) qs.set(k, v); });
    return apiFetch(`/reports/agent-performance?${qs.toString()}`);
  },
  exportCSV: (params) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v) qs.set(k, v); });
    return apiFetch(`/reports/export-csv?${qs.toString()}`);
  },
  exportAudioRecordsCSV: (params) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
    return apiFetch(`/reports/audio-records-csv?${qs.toString()}`);
  },

  
  // Polling
  triggerPoll: () => apiFetch('/polling/trigger', { method: 'POST' }),
  getPollStatus: () => apiFetch('/polling/status'),
  fetchHistorical: (days = 7) => apiFetch(`/calls/fetch-historical?days=${days}`, { method: 'POST' }),
  stopHistoricalFetch: () => apiFetch('/calls/fetch-historical/stop', { method: 'POST' }),
  processBatch: (count = 5) => apiFetch(`/calls/process-batch?count=${count}`, { method: 'POST' }),
  
  // Processing Monitor
  getProcessingStatus: () => apiFetch('/processing/status'),
  getAgentProcessingStats: ({ fromDate = '', toDate = '' } = {}) => {
    const qs = new URLSearchParams();
    if (fromDate) qs.set('from_date', fromDate);
    if (toDate) qs.set('to_date', toDate);
    const suffix = qs.toString();
    return apiFetch(`/processing/agent-stats${suffix ? `?${suffix}` : ''}`);
  },
  getFetchHistory: () => apiFetch('/processing/fetch-logs'),
  processAgentCalls: (agentNumber, count = 10, stage = 'full', { fromDate = '', toDate = '' } = {}) => apiFetch('/processing/agent', {
    method: 'POST',
    body: JSON.stringify({
      agent_number: agentNumber,
      count,
      stage,
      from_date: fromDate,
      to_date: toDate,
    }),
  }),
  retryCall: (callId) => apiFetch(`/calls/${encodeURIComponent(callId)}/retry`, { method: 'POST' }),
  analyzeCall: (callId) => apiFetch(`/calls/${encodeURIComponent(callId)}/analyze`, { method: 'POST' }),
  
  // Manual processing
  processCall: (callId) => apiFetch(`/calls/${encodeURIComponent(callId)}/process`, { method: 'POST' }),
  processSelected: (callIds) => apiFetch('/calls/process-selected', { method: 'POST', body: JSON.stringify({ call_ids: callIds }) }),
  
  // Pending analysis
  getPendingAnalysisCalls: (params) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
    return apiFetch(`/processing/pending-analysis?${qs.toString()}`);
  },

  // Tags
  getTags: ({ refreshKey = '' } = {}) => {
    const qs = new URLSearchParams();
    if (refreshKey) qs.set('_', refreshKey);
    const suffix = qs.toString();
    return apiFetch(`/tags${suffix ? `?${suffix}` : ''}`);
  },
  getTagsSummary: ({ days = 30, from_date = '', to_date = '', refreshKey = '' } = {}) => {
    const qs = new URLSearchParams();
    if (days) qs.set('days', days);
    if (from_date) qs.set('from_date', from_date);
    if (to_date) qs.set('to_date', to_date);
    if (refreshKey) qs.set('_', refreshKey);
    return apiFetch(`/tags/summary?${qs.toString()}`);
  },
  getTagsAnalytics: ({ from_date = '', to_date = '', refreshKey = '' } = {}) => {
    const qs = new URLSearchParams();
    if (from_date) qs.set('from_date', from_date);
    if (to_date) qs.set('to_date', to_date);
    if (refreshKey) qs.set('_', refreshKey);
    return apiFetch(`/tags/analytics?${qs.toString()}`);
  },
  getTagsForCalls: (callIds) => {
    const ids = Array.isArray(callIds) ? callIds.filter(Boolean) : [];
    const qs = new URLSearchParams();
    if (ids.length > 0) {
      qs.set('call_ids', ids.join(','));
    }
    return apiFetch(`/tags/by-call?${qs.toString()}`);
  },
  getTagsForCall: (callId) => apiFetch(`/tags/by-call/${encodeURIComponent(callId)}`),
  getAllTagCalls: (params) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
    return apiFetch(`/tags/calls?${qs.toString()}`);
  },
  getTagCalls: (tagId, params) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
    return apiFetch(`/tags/${tagId}/calls?${qs.toString()}`);
  },
  createTag: (data) => apiFetch('/tags', { method: 'POST', body: JSON.stringify(data) }),
  updateTag: (tagId, data) => apiFetch(`/tags/${tagId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTag: (tagId) => apiFetch(`/tags/${tagId}`, { method: 'DELETE' }),

  // Gemini Processing screen
  getGeminiProcessingSummary: (params) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
    const suffix = qs.toString();
    return apiFetch(`/processing/gemini/summary${suffix ? `?${suffix}` : ''}`);
  },
  getGeminiProcessingCalls: (params) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
    return apiFetch(`/processing/gemini/calls?${qs.toString()}`);
  },
  getGeminiPermanentFailedCalls: (params) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
    return apiFetch(`/processing/gemini/permanent-failed?${qs.toString()}`);
  },
  retryFailedGeminiNow: () =>
    apiFetch('/processing/gemini/failed/retry-now', { method: 'POST' }),
  getGeminiFailedRetryState: () =>
    apiFetch('/processing/gemini/failed/retry-state'),
  runGeminiPipelineNow: ({ lookbackHours, lookbackDays, fromDate, toDate, skipSync, maxCalls } = {}) =>
    apiFetch('/processing/gemini/run-now', {
      method: 'POST',
      body: JSON.stringify({ lookbackHours, lookbackDays, fromDate, toDate, skipSync, maxCalls }),
    }),

  // Manual fetch queue
  getGeminiManualRequests: () => apiFetch('/processing/gemini/manual-requests'),
  createGeminiManualRequest: ({ fromDate, toDate, notes } = {}) =>
    apiFetch('/processing/gemini/manual-requests', {
      method: 'POST',
      body: JSON.stringify({ fromDate, toDate, notes }),
    }),
  reorderGeminiManualRequests: (ids) =>
    apiFetch('/processing/gemini/manual-requests/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ ids }),
    }),
  cancelGeminiManualRequest: (id) =>
    apiFetch(`/processing/gemini/manual-requests/${id}/cancel`, { method: 'POST' }),
  // Contacts
  getContacts: (params) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
    return apiFetch(`/contacts?${qs.toString()}`);
  },
  getContactDocument: async (id) => {
    const res = await fetch(`${API_BASE}/contacts/${encodeURIComponent(id)}/document`, {
      headers: { appid: '3f4b7fd1-d9a0-4b12-9611-88ff5b996d79' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  },

  // Settings
  getSettings: () => apiFetch('/settings'),
  updateSettings: (data) => apiFetch('/settings', { method: 'PUT', body: JSON.stringify(data) }),
  resetPassword: (userId, password) => apiFetch(`/users/${userId}/password`, { method: 'PUT', body: JSON.stringify({ password }) }),
};

export default api;
